import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Truck,
  MapPin,
  Plus,
  Edit,
  Trash2,
  X,
  Calendar,
  User,
  Copy,
  RefreshCw,
  AlertCircle,
  Lock,
  CheckCircle
} from 'lucide-react';
import { getDocuments, createDocument, updateDocument, deleteDocument } from '../../services/firebase';
import { Warehouse, Route, Vehicle, Beat, User as UserType } from '../../types';
import { 
  getBeatConstraints, 
  generateBeatSequentialName, 
  checkSalesmanAvailability,
  getDateOperationalSummary,
  BeatConstraints
} from '../../services/beatValidation';

const BeatManagementSection: React.FC = () => {
  const { currentUser } = useAuth();
  
  // Beat Management states
  const [activeTab, setActiveTab] = useState<'vehicles' | 'beats'>('beats');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [beats, setBeats] = useState<Beat[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  // Route-first: no local vehicle daily status cache; route status derived when needed
  const [loading, setLoading] = useState(false);
  
  // Date-based filtering for beats
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  
  // Filter and Sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [replicating, setReplicating] = useState(false);
  
  // Beat constraint states
  const [beatConstraints, setBeatConstraints] = useState<BeatConstraints>({
    canCreateBeat: true,
    canEditVehicle: true,
    canEditDate: true,
    canEditRoute: true,
    canEditSalesman: true,
    constraints: [],
    warnings: []
  });
  const [validatingConstraints, setValidatingConstraints] = useState(false);

  useEffect(() => {
    loadBeatManagementData();
  }, []);

  // Real-time constraint validation
  useEffect(() => {
    if (activeTab === 'beats' && (showAddModal || showEditModal)) {
      validateBeatConstraints();
    }
  }, [formData.vehicleId, formData.salesmanId, formData.forDate, formData.date, activeTab, showAddModal, showEditModal]);

  const validateBeatConstraints = async () => {
    if (activeTab !== 'beats') return;
    
    setValidatingConstraints(true);
    try {
      const constraints = await getBeatConstraints(
        formData.vehicleId,
        formData.forDate || formData.date,
        formData.salesmanId,
        formData.routeId,
        editingItem?.id, // Pass beatId for edit operations
        !!editingItem // isEdit flag
      );
      setBeatConstraints(constraints);
    } catch (error) {
      console.error('Error validating beat constraints:', error);
    } finally {
      setValidatingConstraints(false);
    }
  };

  const loadBeatManagementData = async () => {
    setLoading(true);
    try {
      const [warehousesData, routesData, vehiclesData, beatsData, usersData] = await Promise.all([
        getDocuments('warehouses'),
        getDocuments('routes'),
        getDocuments('vehicles'),
        getDocuments('beats'),
        getDocuments('users')
      ]);
      setWarehouses(warehousesData as Warehouse[]);
      setRoutes(routesData as Route[]);
      setVehicles(vehiclesData as Vehicle[]);
      setBeats(beatsData as Beat[]);
  setUsers(usersData as UserType[]);
    } catch (err) {
      console.error('Error loading beat management data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter beats by selected date
  const getFilteredBeats = () => {
    return beats.filter(beat => {
      // Use forDate if available, fallback to date field for backward compatibility
      const beatDate = beat.forDate || beat.date;
      return beatDate === selectedDate;
    });
  };

  // Get beats for the previous day
  const getPreviousDayBeats = () => {
    const previousDate = new Date(selectedDate);
    previousDate.setDate(previousDate.getDate() - 1);
    const prevDateStr = previousDate.toISOString().split('T')[0];
    
    return beats.filter(beat => {
      const beatDate = beat.forDate || beat.date;
      return beatDate === prevDateStr;
    });
  };

  // Replicate beats from previous day
  const replicateFromPreviousDay = async () => {
    const previousDayBeats = getPreviousDayBeats();
    
    if (previousDayBeats.length === 0) {
      alert('No beats found for the previous day to replicate.');
      return;
    }

    if (!window.confirm(`This will create ${previousDayBeats.length} new beats for ${selectedDate} based on the previous day. Continue?`)) {
      return;
    }

    setReplicating(true);
    try {
      const replicationPromises = previousDayBeats.map(async beat => {
        // Create new beat object without the id and name fields
        const { id, name, ...beatWithoutId } = beat;
        
        // Generate new sequential name for the new date
        const newBeatName = await generateBeatSequentialName(selectedDate);
        
        const newBeat = {
          ...beatWithoutId,
          name: newBeatName, // Use newly generated name instead of old one
          forDate: selectedDate, // Set to selected date
          date: selectedDate, // Also set legacy date field
          createdAt: new Date(),
          createdBy: currentUser?.id || '',
          updatedAt: new Date(),
          updatedBy: currentUser?.id || ''
        };
        
        return createDocument('beats', newBeat);
      });

      await Promise.all(replicationPromises);
      await loadBeatManagementData(); // Refresh data
      alert(`Successfully replicated ${previousDayBeats.length} beats for ${selectedDate}!`);
    } catch (error) {
      console.error('Error replicating beats:', error);
      alert('Error replicating beats. Please try again.');
    } finally {
      setReplicating(false);
    }
  };

  // Get route operational status from route_daily_status using beat.routeId
  const getRouteOperationalStatus = (routeId: string | undefined, date: string): string => {
    // This component does not subscribe live; for table labels, fallback to 'planning' when unknown
    // A future enhancement can fetch route_daily_status here if needed.
    return 'planning';
  };

  // Get styling for operational status display
  const getOperationalStatusStyle = (status: string) => {
    switch (status) {
      case 'planning':
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Planning' };
      case 'issued':
      case 'issuing':
        return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Issuing' };
      case 'confirmed':
      case 'on_field':
        return { bg: 'bg-green-100', text: 'text-green-800', label: 'On Field' };
      case 'settling':
        return { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Settling' };
      case 'settled':
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Settled' };
      default:
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Planning' };
    }
  };

  // Resolve relationship names for display
  const getResolvedBeat = (beat: Beat) => {
    const route = routes.find(r => (r as any).routeId === (beat as any).routeId);
    const salesman = users.find(u => u.id === beat.salesmanId);
    return {
      ...beat,
      routeName: route?.name || 'Not Assigned',
      salesmanName: salesman?.name || 'Not Assigned'
    };
  };

  // Check if a beat can be edited based on operational status
  const canBeatBeEdited = (beat: Beat): boolean => {
    // Route-first: editing lock is determined by route status; if no route, allow edit
    if (!(beat as any).routeId) return true;
    const operationalStatus = getRouteOperationalStatus((beat as any).routeId, beat.forDate || beat.date || selectedDate);
    return !['on_field', 'settling', 'settled'].includes(operationalStatus);
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const collectionName = activeTab === 'vehicles' ? 'vehicles' : 'beats';
      await deleteDocument(collectionName, id);
      loadBeatManagementData();
      alert('Item deleted successfully!');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      const collectionName = activeTab === 'vehicles' ? 'vehicles' : 'beats';
      
      const data: any = {
        ...formData,
        isActive: formData.isActive !== undefined ? formData.isActive : true,
        createdAt: editingItem ? editingItem.createdAt : new Date(),
        updatedAt: new Date(),
        createdBy: editingItem ? (editingItem.createdBy || currentUser?.id || '') : currentUser?.id || '',
        updatedBy: currentUser?.id || ''
      };

      // Remove vehicle linking for beats as per new flow
      if (collectionName === 'beats') {
        delete data.vehicleId;
        delete data.vehicleName;
      }

      if (editingItem) {
        await updateDocument(collectionName, editingItem.id, data);
        alert('Item updated successfully!');
      } else {
        await createDocument(collectionName, data);
        alert('Item created successfully!');
      }

      loadBeatManagementData();
      setShowEditModal(false);
      setShowAddModal(false);
      setEditingItem(null);
      setFormData({});
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item. Please try again.');
    }
  };

  // Get warehouse-filtered data for dropdowns (Phase 1 filtering)
  const getWarehouseFilteredRoutes = () => {
    if (!currentUser?.warehouseId) return routes;
    return routes.filter(route => route.warehouseId === currentUser.warehouseId);
  };

  const getWarehouseFilteredVehicles = () => {
    if (!currentUser?.warehouseId) return vehicles;
    return vehicles.filter(vehicle => vehicle.warehouseId === currentUser.warehouseId);
  };

  const getWarehouseFilteredSalesmen = () => {
    if (!currentUser?.warehouseId) return users.filter(user => user.role === 'salesman');
    return users.filter(user => 
      user.role === 'salesman' && user.warehouseId === currentUser.warehouseId
    );
  };

  // Get available (unassigned) resources for selected date (Phase 2 filtering)
  const getAvailableRoutes = () => {
    const warehouseFilteredRoutes = getWarehouseFilteredRoutes();
    const selectedFormDate = formData.forDate || formData.date || selectedDate;
    
    // Get all beats for the selected date
    const beatsForDate = beats.filter(beat => {
      const beatDate = beat.forDate || beat.date;
      return beatDate === selectedFormDate;
    });
    
    // Collect already assigned custom routeIds excluding current edit
    const assigned = new Set<string>();
    beatsForDate
      .filter(beat => (editingItem ? beat.id !== editingItem.id : true))
      .forEach((beat: any) => {
        if (beat.routeId) assigned.add(beat.routeId);
      });
    
    // Filter out routes whose custom routeId is already assigned
    return warehouseFilteredRoutes.filter((route: any) => 
      !!route.routeId && !assigned.has(route.routeId)
    );
  };

  const getAvailableVehicles = () => {
    const warehouseFilteredVehicles = getWarehouseFilteredVehicles();
    const selectedFormDate = formData.forDate || formData.date || selectedDate;
    
    // Get all beats for the selected date
    const beatsForDate = beats.filter(beat => {
      const beatDate = beat.forDate || beat.date;
      return beatDate === selectedFormDate;
    });
    
    // Get already assigned vehicle IDs (excluding current beat if editing)
    const assignedVehicleIds = beatsForDate
      .filter(beat => editingItem ? beat.id !== editingItem.id : true)
      .map(beat => beat.vehicleId)
      .filter(Boolean);
    
    // Filter out already assigned vehicles
    return warehouseFilteredVehicles.filter(vehicle => 
      !assignedVehicleIds.includes(vehicle.vehicleId)
    );
  };

  const getAvailableSalesmen = () => {
    const warehouseFilteredSalesmen = getWarehouseFilteredSalesmen();
    const selectedFormDate = formData.forDate || formData.date || selectedDate;
    
    // Get all beats for the selected date
    const beatsForDate = beats.filter(beat => {
      const beatDate = beat.forDate || beat.date;
      return beatDate === selectedFormDate;
    });
    
    // Get already assigned salesman IDs (excluding current beat if editing)
    const assignedSalesmanIds = beatsForDate
      .filter(beat => editingItem ? beat.id !== editingItem.id : true)
      .map(beat => beat.salesmanId)
      .filter(Boolean);
    
    // Filter out already assigned salesmen
    return warehouseFilteredSalesmen.filter(salesman => 
      !assignedSalesmanIds.includes(salesman.id)
    );
  };

  const filterAndSortData = (data: any[]) => {
    let filteredData = [...data];

    if (searchTerm.trim()) {
      filteredData = filteredData.filter(item => {
        const searchableFields = ['name', 'registrationNumber', 'model', 'assignedDriver'];
        return searchableFields.some(field => 
          item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    if (statusFilter !== 'all') {
      filteredData = filteredData.filter(item => {
        const isActive = item.isActive === true;
        return statusFilter === 'active' ? isActive : !isActive;
      });
    }

    return filteredData;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Beat Management</h2>
        <p className="text-gray-600">Manage vehicles and beats.</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { key: 'vehicles', label: 'Vehicles', icon: Truck },
              { key: 'beats', label: 'Beats', icon: MapPin }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-gray-600">Loading data...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Filter Controls */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-64">
                    <input
                      type="text"
                      placeholder="Search by name, registration, model..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="min-w-40">
                    <select
                      value={statusFilter}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onFocus={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 8; } catch {} }}
                      onBlur={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {} }}
                      onChange={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {} setStatusFilter(e.target.value as 'all' | 'active' | 'inactive'); }}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active Only</option>
                      <option value="inactive">Inactive Only</option>
                    </select>
                  </div>

                  {/* Date picker for beats */}
                  {activeTab === 'beats' && (
                    <div className="min-w-48">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  {activeTab === 'vehicles' && `Vehicles (${filterAndSortData(vehicles).length}/${vehicles.length})`}
                  {activeTab === 'beats' && `Beats for ${new Date(selectedDate).toLocaleDateString()} (${filterAndSortData(getFilteredBeats()).length}/${getFilteredBeats().length})`}
                </h3>

                <div className="flex items-center space-x-4">
                  {/* Beat replication button */}
                  {activeTab === 'beats' && (
                    <button
                      onClick={replicateFromPreviousDay}
                      disabled={replicating}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {replicating ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      <span>{replicating ? 'Replicating...' : 'Copy Previous Day'}</span>
                    </button>
                  )}

                  <button
                    onClick={async () => {
                      if (activeTab === 'beats') {
                        // Auto-generate beat name for new beats
                        try {
                          const generatedName = await generateBeatSequentialName(selectedDate);
                          setFormData({ 
                            forDate: selectedDate, 
                            date: selectedDate,
                            name: generatedName 
                          });
                        } catch (error) {
                          console.error('Error generating beat name:', error);
                          // Fallback to default form data if generation fails
                          setFormData({ forDate: selectedDate, date: selectedDate });
                        }
                      } else {
                        setFormData({});
                      }
                      setEditingItem(null);
                      setShowAddModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add New</span>
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-lg border">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        {activeTab === 'vehicles' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registration</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                          </>
                        )}
                        {activeTab === 'beats' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                            {/* Vehicle column removed; assignment happens on Dashboard */}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salesman</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          </>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {activeTab === 'vehicles' && filterAndSortData(vehicles).map((vehicle) => (
                        <tr key={vehicle.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{vehicle.vehicleNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">{vehicle.vehicleId}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">{vehicle.type}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">-</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              vehicle.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {vehicle.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button onClick={() => handleEdit(vehicle)} className="text-blue-600 hover:text-blue-900">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(vehicle.id)} className="text-red-600 hover:text-red-900">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      
                      {activeTab === 'beats' && filterAndSortData(getFilteredBeats()).map((beat) => {
                        const resolvedBeat = getResolvedBeat(beat);
                        const operationalStatus = (beat as any).routeId ? getRouteOperationalStatus((beat as any).routeId, beat.forDate || beat.date || selectedDate) : 'planning';
                        const statusStyle = getOperationalStatusStyle(operationalStatus);
                        return (
                          <tr key={beat.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{beat.name || beat.beatId}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-600">{resolvedBeat.routeName}</td>
                            {/* Vehicle removed from beats; managed via Dashboard vehicle selection */}
                            <td className="px-6 py-4 whitespace-nowrap text-gray-600">{resolvedBeat.salesmanName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                              {(beat.forDate || beat.date) ? new Date(beat.forDate || beat.date).toLocaleDateString() : 'Not Set'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                                {statusStyle.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                              <button 
                                onClick={() => handleEdit(beat)} 
                                disabled={!canBeatBeEdited(beat)}
                                className={canBeatBeEdited(beat) 
                                  ? "text-blue-600 hover:text-blue-900" 
                                  : "text-gray-400 cursor-not-allowed"
                                }
                                title={!canBeatBeEdited(beat) 
                                  ? "Cannot edit: Vehicle is operational" 
                                  : "Edit beat"
                                }
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDelete(beat.id)} className="text-red-600 hover:text-red-900">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Add Modal */}
      {(showEditModal || showAddModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingItem ? `Edit ${activeTab.slice(0, -1)}` : `Add New ${activeTab.slice(0, -1)}`}
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowAddModal(false);
                  setEditingItem(null);
                  setFormData({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Beat Constraints Display */}
              {activeTab === 'beats' && (
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex items-center space-x-2 mb-2">
                    {validatingConstraints ? (
                      <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                    ) : (
                      beatConstraints.canCreateBeat ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )
                    )}
                    <span className="font-medium text-sm">
                      {validatingConstraints ? 'Validating...' : 'Beat Assignment Validation'}
                    </span>
                  </div>

                  {/* Constraints (errors) */}
                  {beatConstraints.constraints.length > 0 && (
                    <div className="mb-2">
                      <div className="text-sm font-medium text-red-700 mb-1">Constraints:</div>
                      {beatConstraints.constraints.map((constraint, index) => (
                        <div key={index} className="flex items-start space-x-2 text-sm text-red-600">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>{constraint}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warnings */}
                  {beatConstraints.warnings.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-yellow-700 mb-1">Warnings:</div>
                      {beatConstraints.warnings.map((warning, index) => (
                        <div key={index} className="flex items-start space-x-2 text-sm text-yellow-600">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>{warning}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {beatConstraints.constraints.length === 0 && beatConstraints.warnings.length === 0 && !validatingConstraints && (
                    <div className="text-sm text-green-600">✓ All validations passed</div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={activeTab === 'beats' ? "Beat name (auto-generated)" : "Enter name"}
                />
              </div>

              {activeTab === 'vehicles' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                    <input
                      type="text"
                      value={formData.registrationNumber || ''}
                      onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter registration number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input
                      type="text"
                      value={formData.model || ''}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter vehicle model"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Driver</label>
                    <input
                      type="text"
                      value={formData.assignedDriver || ''}
                      onChange={(e) => setFormData({...formData, assignedDriver: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter driver name"
                    />
                  </div>
                </>
              )}

              {activeTab === 'beats' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Route
                      {!beatConstraints.canEditRoute && <Lock className="w-3 h-3 inline ml-1 text-gray-400" />}
                    </label>
                    <select
                      value={formData.routeId || ''}
                      disabled={!beatConstraints.canEditRoute}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        !beatConstraints.canEditRoute ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      onFocus={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 8; } catch {} }}
                      onBlur={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {} }}
                      onChange={(e) => { 
                        try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {}
                        const route: any = routes.find(r => (r as any).routeId === e.target.value);
                        setFormData({
                          ...formData,
                          routeId: route?.routeId || '',
                          routeName: route?.name || ''
                        });
                      }}
                    >
                      <option value="">Select Route</option>
                      {getAvailableRoutes().map((route: any) => (
                        <option key={route.routeId} value={route.routeId}>{route.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Vehicle removed from Beat creation/edit; assignment moved to Dashboard */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salesman
                      {!beatConstraints.canEditSalesman && <Lock className="w-3 h-3 inline ml-1 text-gray-400" />}
                    </label>
                    <select
                      value={formData.salesmanId || ''}
                      onChange={(e) => {
                        try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {}
                        const salesman = users.find(u => u.id === e.target.value);
                        setFormData({
                          ...formData, 
                          salesmanId: e.target.value,
                          salesmanName: salesman?.name || ''
                        });
                      }}
                      disabled={!beatConstraints.canEditSalesman}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        !beatConstraints.canEditSalesman ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      onFocus={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 8; } catch {} }}
                      onBlur={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {} }}
                    >
                      <option value="">Select Salesman</option>
                      {getAvailableSalesmen().map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                      {!beatConstraints.canEditDate && <Lock className="w-3 h-3 inline ml-1 text-gray-400" />}
                    </label>
                    <input
                      type="date"
                      value={formData.forDate || formData.date || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        forDate: e.target.value,
                        date: e.target.value // Keep both for compatibility
                      })}
                      disabled={!beatConstraints.canEditDate}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        !beatConstraints.canEditDate ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.isActive !== undefined ? (formData.isActive ? 'true' : 'false') : 'true'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onFocus={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 8; } catch {} }}
                  onBlur={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {} }}
                  onChange={(e) => { try { (e.currentTarget as HTMLSelectElement).size = 1; } catch {} setFormData({...formData, isActive: e.target.value === 'true'}); }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setShowAddModal(false);
                    setEditingItem(null);
                    setFormData({});
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={activeTab === 'beats' && !beatConstraints.canCreateBeat}
                  className={`flex-1 py-2 px-4 rounded-lg ${
                    activeTab === 'beats' && !beatConstraints.canCreateBeat
                      ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  title={
                    activeTab === 'beats' && !beatConstraints.canCreateBeat
                      ? 'Cannot save due to constraint violations'
                      : ''
                  }
                >
                  {activeTab === 'beats' && !beatConstraints.canCreateBeat && <Lock className="w-4 h-4 inline mr-1" />}
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeatManagementSection;